/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";

//Custom libraries
import { SessionErrorDialog } from "../widgets/session-error-modal";
import ConfirmationDialog from "../widgets/confirmation-dialog/ConfirmationDialog";
import { StyledGrid, StyledContainer, StyledTypography, StyledFooter } from "@hcl-commerce-store-sdk/react-component";

//Custom components
import { FooterInput } from "./footerInput/FooterInput";
import { FooterIcons } from "./footerIcons/footerIcons";
import { FooterLinks } from "./footerLinks/FooterLinks";
import { FooterMetodosPago } from "./footerMetodosPago/FooterMetodosPago";
import { FooterCopyright } from "./footerCopyright/FooterCopyright";
import styled from "styled-components";
import { FooterLogo } from "./footerLogo/FooterLogo";

const FooterContainer = styled((props: any) => <StyledGrid {...props} />)`
  ${({ theme }) => `
    background-color: #f8f8f8;
    border-top: solid 1px #263645;

    .layout {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: start;
    }

    .bg_componente {
      min-width: 90%;
      margin-top: 40px;
      background-color: #f8f8f8;
    }

    .titulo_icons {
      margin-bottom: 5px;
    }

    .titulo_icons span {
      color: #263645;
      font-weight: 600;
      font-size: 14px;
      font-family: Inter;
    }

    a:visited {
      color: #000000;
    }

    .contenedor_metodos_de_pago {
      width: 100%;
      margin-top: 10px;
      margin-bottom: 10px;
    }

    .contenedor_iconos_redes_sociales {
      width: 100%;
      margin-top: 10px;
      margin-bottom: 10px;
    }

    .contenedor_footer_links {
      width: 100%;
    }

    //VERSION DESKTOP
    .layout_desktop {
      display: grid;
      grid-template-areas:
        "logo links input"
        "logo links social";
      justify-content: space-between;
      column-gap: 20px;
      row-gap: 10px;
      margin-bottom: 20px;
    }

    //LAYOUT DISPLAY ORDER
    .area_display_logo {
      grid-area: logo;
    }

    .area_display_pago {
      grid-area: pago;
    }

    .area_display_links {
      grid-area: links;
    }

    .area_display_social {
      grid-area: social;
      margin-bottom: 30px;
    }

    .area_display_input {
      grid-area: input;
    }

    .contenedor_logo {
      display: grid;
      grid-template-rows: 30% 70%;
    }

    .ancho_input_container {
      width: 100%;
    }

`}
`;

function Footer(props: any) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <>
      {/* Contenedor padre que hereda clases a los elementos hijos */}
      <FooterContainer>
        <StyledFooter className="bg_componente">
          <StyledContainer className="bg_componente">
            <StyledGrid container className={isMobile ? "layout" : "layout_desktop"}>
              <div className="contenedor_logo area_display_logo">
                {/* <Logo /> */}
                <StyledGrid item xs={12} lg={12} xl={12}>
                  <FooterLogo />
                </StyledGrid>

                {/* Iconos metodos de pago */}
                {!isMobile && (
                  <StyledGrid item xs={12} lg={12} xl={12}>
                    <div className="titulo_icons">
                      <StyledTypography variant="caption">{t("Footer.Onboard.PaymentMethods.title")}</StyledTypography>
                    </div>
                    <FooterMetodosPago />
                  </StyledGrid>
                )}
              </div>

              {/* Input correo del footer */}
              <StyledGrid
                item
                xs={12}
                lg={12}
                xl={12}
                className={isMobile ? "ancho_input_container" : "area_display_input"}>
                <FooterInput />
              </StyledGrid>

              {/* Iconos de redes sociales */}
              <StyledGrid
                item
                xs={12}
                lg={12}
                xl={12}
                className={isMobile ? "contenedor_iconos_redes_sociales" : "area_display_social"}>
                <div className="titulo_icons">
                  <StyledTypography variant="caption">{t("Footer.Onboard.SocialMedia.title")}</StyledTypography>
                </div>
                <FooterIcons />
              </StyledGrid>

              {isMobile && (
                <StyledGrid item xs={12} lg={12} xl={12} className={isMobile && "contenedor_metodos_de_pago"}>
                  <div className="titulo_icons">
                    <StyledTypography variant="caption">{t("Footer.Onboard.PaymentMethods.title")}</StyledTypography>
                  </div>
                  <FooterMetodosPago />
                </StyledGrid>
              )}

              {/* Links del footer (componente)*/}
              <StyledGrid
                item
                xs={12}
                lg={12}
                xl={12}
                className={isMobile ? "contenedor_footer_links" : "area_display_links"}>
                <FooterLinks />
              </StyledGrid>
            </StyledGrid>

            <StyledGrid container className="footer-bottom">
              <StyledGrid item xs={12} lg={12} xl={12}>
                {/* Seccion copyright del footer */}
                <FooterCopyright />
              </StyledGrid>
            </StyledGrid>
          </StyledContainer>
        </StyledFooter>
      </FooterContainer>

      <SessionErrorDialog />
      <ConfirmationDialog />
    </>
  );
}

export default React.memo(Footer);
